import {
  type InferFormValues,
  defineForm,
} from '@/lib/form-contract/define-form';
import { parseMajorToCents } from '@/lib/org-currency';
import { z } from 'zod';

import {
  resolveDeposit,
  serviceForm,
} from '@/features/services-dashboard/service-form';

import type { PackageWithItems } from '../api';
import type { PackageItemChanges } from '../api/sync-package-items';

/** One service and the number of sessions included in the package. */
export interface PackageItemDraft {
  serviceId: string;
  quantity: number;
}

/**
 * The package form, declared ONCE.
 *
 * The payment keys are the SERVICE form's own declarations, reused verbatim: a
 * package is sold on the same terms a service is, rendered by the same
 * `ServiceDepositField` and put on the wire by the same `resolveDeposit`, so the
 * two cannot drift. What the user types is not what reaches the wire — a price
 * in major units becomes cents, a blank
 * validity becomes `null` — so those keys are `derived` and `buildPackagePayload`
 * owns the mapping.
 */
export const packageForm = defineForm({
  fields: {
    name: {
      schema: z.string().min(1, 'Name is required'),
      label: 'Name',
      control: 'text',
      default: '',
      sample: 'Glow course',
    },
    description: {
      schema: z.string(),
      label: 'Description',
      control: 'textarea',
      default: '',
      sample: 'Three facials, booked whenever suits',
    },
    // Rows of services — bespoke, driven by a `fills` override.
    items: {
      schema: z
        .array(
          z.object({
            serviceId: z.string(),
            quantity: z.number().int().min(1).max(999),
          })
        )
        .refine((items) => items.some((item) => item.serviceId), {
          message: 'Add at least one service',
        }),
      label: 'Included services',
      control: 'custom',
      default: [{ serviceId: '', quantity: 1 }],
      sample: [{ serviceId: 'svc-1', quantity: 6 }],
      derived: true,
    },
    // Typed in major units, sent as cents.
    priceRaw: {
      schema: z.string().min(1, 'Price is required'),
      label: 'Package price',
      control: 'text',
      default: '',
      sample: '250.00',
      derived: true,
    },
    // Days from purchase; blank → no expiry (`null`).
    validityDays: {
      schema: z.string(),
      label: 'Valid for',
      control: 'number',
      default: '',
      sample: '90',
      derived: true,
    },
    // Offered for purchase in online booking, at these branches (none: every
    // branch). Both go on the wire as typed.
    sellOnline: {
      schema: z.boolean(),
      label: 'Sell online',
      control: 'custom',
      default: false,
      sample: true,
    },
    locationIds: {
      schema: z.array(z.string()),
      label: 'Branches',
      control: 'custom',
      default: [],
      sample: ['loc-1'],
    },
    requiresDeposit: serviceForm.specs.requiresDeposit,
    depositAmount: serviceForm.specs.depositAmount,
    depositBasis: serviceForm.specs.depositBasis,
    depositPercent: serviceForm.specs.depositPercent,
  },
});

export type PackageFormValues = InferFormValues<typeof packageForm> & {
  /** Preserve a pay-in-full policy until the user changes the payment choice. */
  paymentPolicy?: PackageWithItems['paymentPolicy'];
};

export const packageFormDefaults: PackageFormValues = packageForm.defaults;
export const packageLabels = packageForm.labels;

/** The rows that name a service; an untouched blank row is ignored on save. */
const chosenItems = (values: PackageFormValues) =>
  values.items.filter((item) => item.serviceId);

/**
 * The ONE package wire body (create; update sends it without `items`).
 *
 * Blank description reaches the wire as ABSENCE on create and `null` on update,
 * never `''` — the shape that 400'd `POST /leads`. Throws with the message the
 * operator should read.
 */
export function buildPackagePayload(values: PackageFormValues) {
  const name = values.name.trim();
  if (!name) throw new Error('Name is required');

  const items = chosenItems(values).map((item, sortOrder) => {
    if (
      !Number.isInteger(item.quantity) ||
      item.quantity < 1 ||
      item.quantity > 999
    ) {
      throw new Error('Sessions must be a whole number between 1 and 999');
    }
    return { serviceId: item.serviceId, quantity: item.quantity, sortOrder };
  });
  if (items.length === 0) throw new Error('Add at least one service');

  const priceCents = parseMajorToCents(values.priceRaw);
  if (priceCents == null || priceCents <= 0) {
    throw new Error('Enter a valid price');
  }

  const validityRaw = values.validityDays.trim();
  const validityDays = validityRaw ? Number.parseInt(validityRaw, 10) : null;
  if (
    validityDays !== null &&
    !(
      Number.isInteger(validityDays) &&
      validityDays >= 1 &&
      validityDays <= 3650
    )
  ) {
    throw new Error('Valid for must be between 1 and 3650 days');
  }

  const deposit = resolveDeposit(values);

  return {
    name,
    description: values.description.trim() || undefined,
    priceCents,
    validityDays,
    items,
    requiresDeposit: deposit.requiresDeposit,
    paymentPolicy: deposit.paymentPolicy,
    depositAmountCents: deposit.depositAmountCents,
    depositBasis: deposit.depositBasis,
    depositPercent: deposit.depositPercent,
    sellOnline: values.sellOnline,
    locationIds: values.locationIds,
    sortOrder: 0,
    isActive: true,
  };
}

/**
 * `PUT /packages/:id` — the same body minus the services, which sync apart, and
 * minus `sortOrder`/`isActive`, which this editor does not own: an edit must
 * not reactivate or reorder a package.
 */
export function buildUpdatePackagePayload(values: PackageFormValues) {
  const {
    items: _items,
    sortOrder: _sortOrder,
    isActive: _isActive,
    description,
    ...rest
  } = buildPackagePayload(values);
  return { ...rest, description: description ?? null };
}

/**
 * What to add, change and remove so a saved package holds the edited services.
 * Matched by service (a package lists each service once); order is the row order.
 */
export function diffPackageItems(
  saved: PackageWithItems['items'],
  values: PackageFormValues
): PackageItemChanges {
  const wanted = chosenItems(values).map((item, sortOrder) => ({
    serviceId: item.serviceId,
    quantity: item.quantity,
    sortOrder,
  }));
  const savedByService = new Map(saved.map((item) => [item.serviceId, item]));
  const wantedServices = new Set(wanted.map((item) => item.serviceId));

  return {
    add: wanted.filter((item) => !savedByService.has(item.serviceId)),
    update: wanted.flatMap((item) => {
      const existing = savedByService.get(item.serviceId);
      if (!existing) return [];
      if (
        existing.quantity === item.quantity &&
        existing.sortOrder === item.sortOrder
      )
        return [];
      return [
        {
          itemId: existing.id,
          quantity: item.quantity,
          sortOrder: item.sortOrder,
        },
      ];
    }),
    remove: saved
      .filter((item) => !wantedServices.has(item.serviceId))
      .map((item) => item.id),
  };
}
